import htmlToText from 'html2plaintext';
import { toggleMark } from 'prosemirror-commands';
import {
  DOMSerializer,
  Fragment,
  MarkType,
  NodeType,
  Slice,
} from 'prosemirror-model';
import { liftListItem, wrapInList } from 'prosemirror-schema-list';
import { Command, EditorState, Transaction } from 'prosemirror-state';
import schema from './schema';

export function isBulletListNode(node) {
  return node.type.name === 'bullet_list';
}

export function isOrderedListNode(node) {
  return node.type.name === 'ordered_list';
}

export function isListNode(node) {
  if (node instanceof Node) {
    return isBulletListNode(node) || isOrderedListNode(node);
  }
  return false;
}

export function compareNumber(a, b) {
  return a > b ? 1 : a < b ? -1 : 0;
}

export const isUrl = url =>
  // tslint:disable-next-line:max-line-length
  /(?:^(https?|s?ftp):\/\/)?(((([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(%[\da-f]{2})|[!\$&'\(\)\*\+,;=]|:)*@)?(((\d|[1-9]\d|1\d\d|2[0-4]\d|25[0-5])\.(\d|[1-9]\d|1\d\d|2[0-4]\d|25[0-5])\.(\d|[1-9]\d|1\d\d|2[0-4]\d|25[0-5])\.(\d|[1-9]\d|1\d\d|2[0-4]\d|25[0-5]))|((([a-z]|\d|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(([a-z]|\d|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])*([a-z]|\d|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])))\.)+(([a-z]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(([a-z]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])*([a-z]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])))\.?)(:\d*)?)(\/((([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(%[\da-f]{2})|[!\$&'\(\)\*\+,;=]|:|@)+(\/(([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(%[\da-f]{2})|[!\$&'\(\)\*\+,;=]|:|@)*)*)?)?(\?((([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(%[\da-f]{2})|[!\$&'\(\)\*\+,;=]|:|@)|[\uE000-\uF8FF]|\/|\?)*)?(#((([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(%[\da-f]{2})|[!\$&'\(\)\*\+,;=]|:|@)|\/|\?)*)?$/i.test(
    url,
  );

export function findNodesWithSameMark(doc, from, to, markType) {
  let ii = from;
  const finder = mark => mark.type.name === markType;
  let firstMark = null;
  let fromNode = null;

  if (from === to) {
    const node = doc.nodeAt(from);
    if (!node || !node.marks) {
      return null;
    }
    const mark = node.marks.find(finder);
    if (!mark && !firstMark) {
      return null;
    }
    return mark;
  }

  while (ii < to) {
    const node = doc.nodeAt(ii);
    if (!node || !node.marks) {
      return null;
    }
    const mark = node.marks.find(finder);
    if (!mark && !firstMark) {
      return null;
    }
    fromNode = fromNode || node;
    firstMark = firstMark || mark;
    ii = ii + 1;
  }

  return firstMark;
}

export function useLinkHref(state) {
  const { doc, selection } = state;
  const { from, to } = selection;
  const mark = findNodesWithSameMark(doc, from, to, 'link');
  return mark ? mark.attrs.href : '';
}

/* modifier check */
export const isMarkActive =
  (mark: MarkType) =>
  (state: EditorState): boolean => {
    const { from, $from, to, empty } = state.selection;
    return empty
      ? !!mark.isInSet(state.storedMarks || $from.marks())
      : state.doc.rangeHasMark(from, to, mark);
  };

// https://discuss.prosemirror.net/t/list-type-toggle/948/2
export const isBlockActive = (type: NodeType) => (state: EditorState) => {
  const { $from, $to, to } = state.selection;

  const range = $from.blockRange($to);

  return (
    !!range?.depth &&
    $from
      .node(range.depth > 1 ? range.depth - 1 : range.depth)
      ?.hasMarkup?.(type)
  );
};

export const isBold = isMarkActive(schema.marks.strong);

export const isItalic = isMarkActive(schema.marks.em);

export const isUnderline = isMarkActive(schema.marks.underline);

export const isStrikethrough = isMarkActive(schema.marks.strikethrough);

export const isLink = isMarkActive(schema.marks.link);

export const isOrderedList = isBlockActive(schema.nodes.ordered_list);

export const isUnorderedList = isBlockActive(schema.nodes.bullet_list);

/* toggle commands.ts */
export const toggleMarkCommand = (mark: MarkType): Command => {
  return (
    state: EditorState,
    dispatch: ((tr: Transaction) => void) | undefined,
  ) => toggleMark(mark)(state, dispatch);
};

export const toggleUnorderedList = (state: EditorState, dispatch) => {
  const isActive = isUnorderedList(state);
  if (isActive) {
    // https://discuss.prosemirror.net/t/toggle-bullet-list-is-not-working/1714/2
    liftListItem(schema.nodes.list_item)(state, dispatch);
    return;
  }

  wrapInList(schema.nodes.bullet_list)(state, dispatch);
};

export const toggleOrderedList = (state: EditorState, dispatch) => {
  const isActive = isOrderedList(state);
  if (isActive) {
    // https://discuss.prosemirror.net/t/toggle-bullet-list-is-not-working/1714/2
    liftListItem(schema.nodes.list_item)(state, dispatch);
    return;
  }

  wrapInList(schema.nodes.ordered_list)(state, dispatch);
};

export const canInsert = (type: NodeType) => (state: EditorState) => {
  const { $from } = state.selection;
  for (let d = $from.depth; d >= 0; d -= 1) {
    const index = $from.index(d);
    if ($from.node(d).canReplaceWith(index, index, type)) {
      return true;
    }
  }
  return false;
};

export const insertText = (text, marks) => (state: EditorState, dispatch) => {
  const node = state.schema.text(text, marks);
  const frag = Fragment.from(node);
  const slice = new Slice(frag, 0, 0);
  const transaction = state?.tr?.replaceRange(
    state?.selection.from,
    state?.selection.to,
    slice,
  );

  state.apply(transaction);
  dispatch(transaction);

  return node;
};

export const getHTMLStringFromState = (state: EditorState) => {
  const fragment = DOMSerializer.fromSchema(state.schema).serializeFragment(
    state.doc.content,
  );
  const div = document.createElement('div');
  div.appendChild(fragment);
  return div.innerHTML;
};

export const getHTMLText = html => htmlToText(html);
